import React, { createContext, useState, useContext, useEffect } from 'react';
import { Snackbar, Alert } from '@mui/material';
import axios from 'axios';
import { getErrorMessage } from './utils/apiError';

const ErrorContext = createContext();

export const useError = () => {
  const context = useContext(ErrorContext);
  if (!context) throw new Error('useError must be used within an ErrorProvider');
  return context;
};

export const ErrorProvider = ({ children }) => {
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);

  const showError = (message) => {
    console.error('[Global Error Handler]:', message);
    setError(message);
    setOpen(true);
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpen(false);
  };

  useEffect(() => {
    // 1. Axios Interceptor for API Errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (err) => {
        // Filter out 401 (Auth) and 403 (Upgrade Required) as they are handled by route guards or local logic
        if (err.response?.status !== 401 && err.response?.status !== 403) {
          showError(getErrorMessage(err));
        }
        return Promise.reject(err);
      }
    );

    // 2. Unhandled Promise Rejections
    const handleRejection = (event) => {
      showError(`Unhandled Promise: ${event.reason?.message || 'Unknown error'}`);
    };

    // 3. Global Runtime Errors
    const handleRuntimeError = (event) => {
      // Filter out ResizeObserver noise already handled in index.js
      if (event.message?.includes('ResizeObserver')) return;
      showError(`Runtime Error: ${event.message}`);
    };

    window.addEventListener('unhandledrejection', handleRejection);
    window.addEventListener('error', handleRuntimeError);

    return () => {
      axios.interceptors.response.eject(interceptor);
      window.removeEventListener('unhandledrejection', handleRejection);
      window.removeEventListener('error', handleRuntimeError);
    };
  }, []);

  return (
    <ErrorContext.Provider value={{ showError }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleClose} severity="error" variant="filled" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </ErrorContext.Provider>
  );
};